import React from 'react'
import { FormattedDate } from 'react-intl'
import { View } from 'react-native'
import { useNavigate } from 'react-router-dom'

import { isAfter, isBefore, subDays, subMinutes, subMonths } from 'date-fns'
import { zonedTimeToUtc } from 'date-fns-tz'
import { isNil, union } from 'lodash-es'
import styled, { useTheme } from 'styled-components/native'

import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { getProgramNameFromId } from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/programHelpers'
import { Appointment } from '@lyrahealth-inc/shared-app-logic/src/models/appointment/Appointment'
import {
  AIDraftStatus,
  AppointmentSummary,
  noteSummaryStatusText,
} from '@lyrahealth-inc/shared-app-logic/src/models/appointmentSummary/AppointmentSummary'
import { CalendarAppointmentsView } from '@lyrahealth-inc/shared-app-logic/src/models/calendar/CalendarV2'
import { ClientListClientObject } from '@lyrahealth-inc/shared-app-logic/src/models/client/ClientListClient'
import { Note, NoteStatusStates, NoteTypes } from '@lyrahealth-inc/shared-app-logic/src/models/notes/Notes'
import { LTProgramName, ProgramNames } from '@lyrahealth-inc/shared-app-logic/src/models/programs/Programs'
import RefreshButton from '@lyrahealth-inc/ui-core/lib/atoms/buttons/refreshButton/RefreshButton'
import Dashlet from '@lyrahealth-inc/ui-core/lib/molecules/layouts/dashlet/Dashlet'
import { AIStatusBadge } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/aiStatusBadge/AIStatusBadge'
import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { IconBadge } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/iconBadge/IconBadge'
import { ChevronIcon, ChevronIconDirection } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/ChevronIcon'
import { PressableOpacity } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/pressableOpacity/PressableOpacity'
import { BadgeShape, BadgeVariant } from '@lyrahealth-inc/ui-core-crossplatform/src/badge/constants'
import { BodyTextSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { ThemeType } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/themes/ThemeProvider'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { actions, mixpanelEvents, sessionNoteTypes } from '../../../../../mixpanel/mixpanelConstants'
import { track } from '../../../../../mixpanel/mixpanelTracking'
import { appointmentStatuses, programConfig } from '../../../common/constants/appConstants'
import { CLIENT_NOTES } from '../../../common/constants/routingConstants'
import { getClientFullName } from '../../../common/utils/utils'
import { useAppDispatch } from '../../../data/store'
import { useGetAppointmentSummariesQuery, useGetNotesQuery } from '../../clientNotes/data/notesApi'
import { useGetAppointmentsQuery } from '../../clients/clientDetails/data/appointmentsApi'
import { setSelectedClientId } from '../../clients/clientDetails/data/ltClientSlice'
import { isProviderAppointment } from '../dashboardUtils'
import { useDashboardClientsData } from '../hooks/useDashboardClientsData'

const DashletItemContainer = styled.View<{ theme: ThemeType; first: boolean }>(({ theme, first }) => ({
  border: 0,
  width: '100%',
  padding: theme.spacing['4px'],
  borderTopWidth: first ? 0 : '1px',
  borderTopColor: theme.colors.borderDefault,
}))

const DashletItemContent = styled(PressableOpacity)({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  minHeight: '61px',
  height: 'auto',
  backgroundColor: 'white',
  border: 'none',
  margin: '0',
})

const AppointmentInfo = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  flexDirection: theme.breakpoints.isMinWidthDesktopL ? 'row' : 'column',
}))

const AppointmentTime = styled.View({
  flexDirection: 'column',
})

const NoteInfo = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  flexDirection: 'column',
  marginLeft: theme.breakpoints.isMinWidthDesktopL ? theme.spacing['32px'] : 0,
  alignItems: 'flex-start',
}))

const BadgeSectionContainer = styled.View({
  flexDirection: 'row',
})

const BadgeContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  width: 'fit-content',
  padding: `${theme.spacing['8px']} 0`,
  marginRight: theme.spacing['8px'],
}))

const RightContainer = styled.View({
  flexDirection: 'row',
})

const EmptyDashlet = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  textAlign: 'center',
  margin: `${theme.spacing['16px']} 0`,
}))

const UnfinishedNotesDashlet: React.FC<UnfinishedNotesDashletProps> = ({ providerId }) => {
  const { clients, primaryClients, isSuccess: clientsFetched } = useDashboardClientsData()
  const { data: appointments = [] } = useGetAppointmentsQuery({
    providerId: providerId,
    durationRange: { before: { months: 6 } },
    view: CalendarAppointmentsView.CALENDAR,
  })
  const {
    data: allNotes,
    refetch: refetchNotes,
    isFetching: fetchingNotes,
    isLoading: loadingNotes,
    isSuccess: notesFetched,
  } = useGetNotesQuery({
    providerId: providerId,
    months: 7,
    note_types: [NoteTypes.INITIAL, NoteTypes.SESSION, NoteTypes.REFERRAL],
    excludedFields: ['body', 'content_meta_data'],
  })
  const navigate = useNavigate()
  const { colors } = useTheme()
  const dispatch = useAppDispatch()

  const { enableAINotesFeatures, isPreferredNameEnabled, showAINotesFeatures, showAINotesForLCMAndCLE } = useFlags()

  const {
    data: appointmentSummaries,
    refetch: refetchAppointmentSummaries,
    isFetching: fetchingAppointmentSummaries,
    isLoading: loadingAppointmentSummaries,
    isSuccess: appointmentSummariesFetched,
  } = useGetAppointmentSummariesQuery(
    {
      providerId,
      statusOnly: true,
    },
    { skip: !enableAINotesFeatures },
  )

  // To avoid showing appointments that happened before we added Notes to our platform, we will filter out any appoitnments that happened before 2021
  // Appointments without an episode can never get an LT note, so they can't be finished here
  const providerAppointments = appointments.filter(
    (appointment) => isProviderAppointment(appointment, providerId) && !isNil(appointment.episodeId),
  )

  const pastAppointmentsWithoutNotes = providerAppointments.filter((appt: Appointment) => {
    const apptDateTimeToUTC = zonedTimeToUtc(new Date(`${appt.startDate} ${appt.startTime}`), appt.timeZone)
    return (
      ![appointmentStatuses.canceled, appointmentStatuses.missed].includes(appt.appointmentStatus) &&
      isBefore(apptDateTimeToUTC, new Date()) &&
      !allNotes?.find(({ appointment_id }: any) => appointment_id === appt.appointmentId) &&
      isAfter(apptDateTimeToUTC, subMonths(new Date(), 6))
    )
  })

  const appointmentsWithDraftNotes = providerAppointments.filter((appt: Appointment) => {
    return (
      ![appointmentStatuses.canceled, appointmentStatuses.missed].includes(appt.appointmentStatus) &&
      allNotes?.find((note: Note) => note.appointment_id === appt.appointmentId && note.status === 'draft') &&
      isAfter(new Date(appt.startDate), subMonths(new Date(), 6))
    )
  })

  const referralDraftNotes = allNotes?.filter(
    (note: Note) =>
      note.status === NoteStatusStates.DRAFT &&
      note.note_type === NoteTypes.REFERRAL &&
      primaryClients.some(({ id }) => note.patient_id === id),
  )

  const incompleteAndMissingSessionNotes = union(pastAppointmentsWithoutNotes, appointmentsWithDraftNotes)
    .filter((appt: Appointment) => clients?.some(({ id }: { id: string }) => appt.userId === id))
    .sort((a: Appointment, b: Appointment) => {
      const firstDateTimeToUTC = zonedTimeToUtc(new Date(`${a.startDate} ${a.startTime}`), a.timeZone)
      const secondDateTimeToUTC = zonedTimeToUtc(new Date(`${b.startDate} ${b.startTime}`), b.timeZone)
      return isBefore(firstDateTimeToUTC, secondDateTimeToUTC) ? -1 : 1
    })

  const getAppointmentListItemData = (appt: Appointment) => {
    const clientForAppointment = clients?.find(({ id }: { id: string }) => appt.userId === id)
    const appointmentTime = zonedTimeToUtc(`${appt.startDate} ${appt.startTime}`, appt.timeZone)
    const noteType = appt.sessionNumber === 0 ? sessionNoteTypes.initial : sessionNoteTypes.session
    const threeDaysAndAppointmentMinutesEarlier = subMinutes(subDays(new Date(), 3), appt.appointmentDuration)
    const overdueNote = isBefore(appointmentTime, threeDaysAndAppointmentMinutesEarlier)
    const programName = getProgramNameFromId(appt.programId) ?? ''
    const aiSummaryStatus =
      programConfig[programName as LTProgramName]?.aiNotesEnabled &&
      (programName !== ProgramNames.MedicationManagement ||
        (programName === ProgramNames.MedicationManagement && showAINotesForLCMAndCLE)) &&
      (programName !== ProgramNames.ClinicalLeaveEvaluation ||
        (programName === ProgramNames.ClinicalLeaveEvaluation && showAINotesForLCMAndCLE)) &&
      (programName !== ProgramNames.SubstanceUseEvaluation ||
        (programName === ProgramNames.SubstanceUseEvaluation && showAINotesForLCMAndCLE)) &&
      appointmentSummaries?.find(
        (summary: AppointmentSummary) => summary.appointment_id === appt.appointmentId.toString(),
      )?.status

    return {
      client: clientForAppointment,
      time: appointmentTime,
      sessionNumber: appt.sessionNumber,
      overdueNote,
      aiSummaryStatus,
      noteType,
      appointmentId: appt.appointmentId.toString(),
    }
  }

  const getReferralNoteListItemData = (note: Note) => {
    const clientForNote = clients?.find(({ id }: { id: string }) => note.patient_id === id)
    const lastEditTime = new Date(note.update_date)
    const overdueNote = isBefore(new Date(note?._create_date), subDays(new Date(), 3))

    return { client: clientForNote, time: lastEditTime, overdueNote, noteType: sessionNoteTypes.referral }
  }

  const renderListItem = (
    {
      client,
      time,
      sessionNumber,
      overdueNote = false,
      aiSummaryStatus,
      noteType,
      appointmentId,
    }: {
      client: ClientListClientObject | undefined
      time: Date
      sessionNumber?: number
      overdueNote?: boolean
      aiSummaryStatus?: AIDraftStatus
      noteType?: string
      appointmentId?: string
    },
    index: number,
  ) => {
    if (!client) {
      return null
    }
    const onClickHandler = () => {
      track({
        event: mixpanelEvents.BUTTON_PRESS,
        action: actions.UNFINISHED_NOTES_DASHLET_OPEN_NOTE,
        details: {
          ...(aiSummaryStatus && { summaryStatus: noteSummaryStatusText[aiSummaryStatus] }),
          overdueNote: overdueNote,
          noteType: noteType,
          appointmentId,
        },
      })
      dispatch(setSelectedClientId(client.id))
      navigate(CLIENT_NOTES.route)
    }

    return (
      <DashletItemContainer
        key={`${client.first_name}${time}`}
        testID={tID(`UnfinishedNotes-list-item-${client.first_name}-${time}`)}
        first={index === 0}
      >
        <DashletItemContent key={`${client.first_name}${time}`} onPress={onClickHandler}>
          <View>
            <AppointmentInfo>
              <AppointmentTime>
                <BodyText
                  text={<FormattedDate value={time} year='numeric' month='2-digit' day='2-digit' />}
                  size={BodyTextSize.SMALL}
                  color={colors.textPrimary}
                />
                <BodyText
                  text={<FormattedDate value={time} hour='numeric' minute='2-digit' />}
                  size={BodyTextSize.SMALL}
                  color={colors.textInactive}
                />
              </AppointmentTime>
              <NoteInfo>
                <BodyText
                  text={getClientFullName(client, isPreferredNameEnabled)}
                  size={BodyTextSize.SMALL}
                  color={colors.textPrimary}
                />
                <BodyText
                  text={!isNil(sessionNumber) && sessionNumber >= 0 ? `Session ${sessionNumber}` : 'Care Referral'}
                  size={BodyTextSize.SMALL}
                  color={colors.textInactive}
                />
              </NoteInfo>
            </AppointmentInfo>
            <BadgeSectionContainer>
              {showAINotesFeatures && aiSummaryStatus && (
                <BadgeContainer>
                  <AIStatusBadge status={aiSummaryStatus} shape={BadgeShape.RECT} />
                </BadgeContainer>
              )}
              {overdueNote && (
                <BadgeContainer>
                  <IconBadge
                    text='overdue'
                    variant={BadgeVariant.ALERT}
                    shape={BadgeShape.RECT}
                    testID={tID('IconBadge-overdue')}
                  />
                </BadgeContainer>
              )}
            </BadgeSectionContainer>
          </View>
          <RightContainer>
            <ChevronIcon direction={ChevronIconDirection.RIGHT} size={13} fillColor={colors.textPrimary} />
          </RightContainer>
        </DashletItemContent>
      </DashletItemContainer>
    )
  }
  const isRefreshingNotes =
    (fetchingNotes && notesFetched) || (fetchingAppointmentSummaries && appointmentSummariesFetched)

  return (
    <View testID={tID('UnfinishedNotesDashlet-container')}>
      <Dashlet
        title={`Unfinished notes${
          clientsFetched ? ` (${incompleteAndMissingSessionNotes.length + (referralDraftNotes?.length ?? 0)})` : ''
        }`}
        buttons={
          <RefreshButton
            size={'20'}
            isLoading={isRefreshingNotes}
            onClick={() => {
              if (enableAINotesFeatures) {
                refetchAppointmentSummaries()
              }
              refetchNotes()
            }}
          />
        }
        isLoading={!clientsFetched || loadingNotes || loadingAppointmentSummaries}
      >
        {incompleteAndMissingSessionNotes.length === 0 && referralDraftNotes?.length === 0 ? (
          <EmptyDashlet>No unfinished notes</EmptyDashlet>
        ) : (
          union(
            referralDraftNotes?.map(getReferralNoteListItemData),
            incompleteAndMissingSessionNotes.map(getAppointmentListItemData),
          ).map(renderListItem)
        )}
      </Dashlet>
    </View>
  )
}

type UnfinishedNotesDashletProps = {
  providerId: string
}

export default UnfinishedNotesDashlet
